import React, { useState, useEffect, Fragment } from "react";
import { Row, Col, Table, Pagination, Button, Form } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "../../hooks/useTranslation";
import { showToast } from "../../Redux/toastReducer";
import i18next from 'i18next';
import apiService from "../../services/api.service";
import TableSearchBar from "../MyAccountComponents/TableSearchBar";


function CompanyGreetingsTable({  }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company.company);
  const companyUser = useSelector((state) => state.company.companyUser);
  const pageSize = 10;
  const emptyGreeting = { name: '', text: '', isVisible: false };
  const currentLang = i18next.language; // "eng", "heb", etc.

  const [greetings, setGreetings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filteredCurrentPage, setFilteredCurrentPage] = useState(1);
  const [filteredGreetings, setFilteredGreetings] = useState([]);
  const [newGreeting, setNewGreeting] = useState(emptyGreeting);
  const [editingGreetingId, setEditingGreetingId] = useState(null);
  const [editGreetingData, setEditGreetingData] = useState({});

  const isFiltered = filteredGreetings.length !== greetings.length;
  const filteredPages = Math.ceil(filteredGreetings.length / pageSize);

  const displayPage  = isFiltered ? filteredCurrentPage : currentPage;
  const displayPages = isFiltered ? filteredPages : pages;
  const setDisplayPage = isFiltered ? setFilteredCurrentPage : setCurrentPage;

  const displayedGreetings = isFiltered
    ? filteredGreetings.slice((filteredCurrentPage - 1) * pageSize, filteredCurrentPage * pageSize)
    : greetings.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getGreetingText = (greeting) => greeting.translations?.find(t => t.isoLanguageCode == currentLang)?.greetingText ?? '';

  useEffect(() => {
    fetchGreetings();
  }, []);

  useEffect(() => {
    setFilteredGreetings(greetings);
    setFilteredCurrentPage(1);
  }, [greetings]);

  const fetchGreetings = () => {
    apiService.getGreetings({ companyId: companyUser.companyId, includeTranslations: true })
      .then((response) => {
        if (response.data && !response.data.HasErrors && response.data.data && response.data.data.length > 0) {
          setGreetings(response.data.data);
          setPages(Math.ceil(response.data.data.length / pageSize));
        } else {
          setGreetings([]);
        }
      },
      (error) => {}
    );
  };

  

  /* Rendering */

  const renderPaginationItems = () => {
    const items = [];
    for (let number = 1; number <= displayPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === displayPage}
          onClick={() => setDisplayPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  const renderGreetings = (list) => {
    return list.map((greeting, index) => {
      const isEditing = greeting.greetingId === editingGreetingId;

      if (isEditing) {
        return (
          <Fragment key={greeting.greetingId || index}>
            <tr className="greeting-item greeting-item--editing">
              <td>
                <Form.Control
                  type="text"
                  placeholder={t("GreetingName")}
                  value={editGreetingData.greetingName}
                  onChange={(e) => setEditGreetingData(prev => ({ ...prev, greetingName: e.target.value }))}
                />
              </td>
              <td>
                <Form.Control
                  as="textarea" rows={3} 
                  placeholder={t("GreetingText")}
                  value={editGreetingData.greetingText}
                  onChange={(e) => setEditGreetingData(prev => ({ ...prev, greetingText: e.target.value }))}
                />
              </td>
              <td>
                <div className="row-btns">
                  <Button variant="secondary" className="account-btn row-btn-save" onClick={handleSaveEdit}><img src="../images/save-icon.svg" alt=""/></Button>
                  <Button variant="secondary" className="account-btn row-btn-delete" onClick={handleCancelEdit}><img src="../images/close-icon.svg" alt=""/></Button>
                </div>
              </td>
            </tr>
          </Fragment>
        );
      }

      return (
        <Fragment key={greeting.greetingId || index}>
          <tr className="greeting-item" greetinng-id={greeting.greetingId}>
            <td aria-label={`${t("GreetingName")} ${greeting.greetingName}`}>
              {greeting.greetingName}
            </td>
            <td aria-label={`${t("GreetingText")} ${getGreetingText(greeting)}`}>
              {getGreetingText(greeting)}
            </td>
            <td>
              <div className="row-btns">
                <Button variant="secondary" className="account-btn row-btn-edit" onClick={() => handleEditGreeting(greeting)}><img src="../images/edit-icon.svg" alt=""/></Button>
                <Button variant="secondary" className="account-btn row-btn-delete" onClick={() => handleDeleteGreeting(greeting)}><img src="../images/trash-icon.svg" alt=""/></Button>
              </div>
            </td>
          </tr>
        </Fragment>
      );
    });
  };


  /* Button Handlers */

  const handleToggleNewGreeting = () => {
    if (newGreeting.isVisible) {
      setNewGreeting(emptyGreeting);
      return;
    }
    setNewGreeting(prev => ({ ...prev, isVisible: true }));
  };
  
  const handleSaveNewGreeting = () => {
    const req = {
      greetingId: null,
      chainId: company.chains[0]?.chainId ?? 0,
      companyId: companyUser.companyId,
      greetingName: newGreeting.name,
      EventTypeId: 0,
      isActive: true,
      isDeleted: false,
      translations: [
          {   
            isoLanguageCode: currentLang,
            greetingText: newGreeting.text
          }
      ]
    };

    apiService.setGreeting(req)
      .then((response) => {
        if (response.data && !response.data.HasErrors) {
          setGreetings(prev => [{ ...req, greetingId: response.data.data }, ...prev]);
          setFilteredGreetings(prev => [{ ...req, greetingId: response.data.data }, ...prev]);
          setNewGreeting(emptyGreeting);
          dispatch(showToast({ message: t("GreetingSaveSuccess"), severity: "success" }));
        } else {
          dispatch(showToast({ message: t("GreetingSaveFailure"), severity: "error" }));
        }
      },
      (error) => {
        dispatch(showToast({ message: t("GreetingSaveFailure"), severity: "error" }));
      }
    );
  };

  const handleDeleteGreeting = (greeting) => {
    if (!greeting || !greeting.greetingId) return;

    apiService.deleteGreeting(greeting.greetingId)
      .then((response) => {
        if (response.data && !response.data.HasErrors) {
          setGreetings(prev => prev.filter(g => g.greetingId !== greeting.greetingId));
          setFilteredGreetings(prev => prev.filter(g => g.greetingId !== greeting.greetingId));
          dispatch(showToast({ message: t("GreetingDeleteSuccess"), severity: "success" }));
        } else {
          dispatch(showToast({ message: t("GreetingDeleteFailure"), severity: "error" }));
        }
      },
      (error) => {
        dispatch(showToast({ message: t("GreetingDeleteFailure"), severity: "error" }));
      }
    );
  };

  const handleEditGreeting = (greeting) => {
    setEditingGreetingId(greeting.greetingId);
    setEditGreetingData({
      greetingName: greeting.greetingName,
      greetingText: getGreetingText(greeting),
    });
  };

  const handleCancelEdit = () => {
    setEditingGreetingId(null);
    setEditGreetingData({});
  };

  const handleSaveEdit = () => {
    const req = {
      "companyId": companyUser.companyId,
      "greetingId": editingGreetingId,
      "greetingName": editGreetingData.greetingName,
      "translations": [
        {
          "greetingId": editingGreetingId, 
          "isoLanguageCode": currentLang,
          "greetingText": editGreetingData.greetingText
        }
      ],
    };

    apiService.setGreeting(req)
      .then((response) => {
        if (response.data && !response.data.HasErrors) {
          setGreetings(prev => prev.map(g =>
            g.greetingId === editingGreetingId
              ? { ...g, ...req }
              : g
          ));
          setFilteredGreetings(prev => prev.map(g =>
            g.greetingId === editingGreetingId
              ? { ...g, ...req }
              : g
          ));
          handleCancelEdit();
          dispatch(showToast({ message: t("GreetingEditSuccess"), severity: "success" }));
        } else {
          dispatch(showToast({ message: t("GreetingEditFailure"), severity: "error" }));
        }
      },
      (error) => {
        dispatch(showToast({ message: t("GreetingEditFailure"), severity: "error" }));
      }
    );
  };

  return (
    <>
      <h4 aria-label={t("MyGreetings")}>{t("MyGreetings")}</h4>
      <Row>
        <Col className="flex-row d-flex gap-3">
          <div className="table-actions">
            <TableSearchBar dataList={greetings} setFilteredDataList={setFilteredGreetings} onPageReset={() => setFilteredCurrentPage(1)} />
            <Button variant="secondary" className="account-btn row-btn-add" onClick={handleToggleNewGreeting}><img src="../images/plus-icon.svg" alt=""/>{t("AddGreeting")}</Button>
          </div>
        </Col>
      </Row>

      <Table hover className="greetingsTable" responsive>
        <thead>
          <tr>
            <th style={{width: "30%"}}>{t("GreetingName")}</th>
            <th>{t("GreetingText")}</th>
            <th style={{width: "10%"}}>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {newGreeting.isVisible && (
            <tr className="greeting-item">
              <td>
                <Form.Control type="text" placeholder={t("GreetingName")} value={newGreeting.name} onChange={(e) => setNewGreeting(prev => ({ ...prev, name: e.target.value }))} />
              </td>
              <td>
                <Form.Control type="text" placeholder={t("GreetingText")} value={newGreeting.text} onChange={(e) => setNewGreeting(prev => ({ ...prev, text: e.target.value }))} />
              </td>
              <td>
                <div className="row-btns">
                  <Button variant="secondary" className="account-btn row-btn-save" onClick={handleSaveNewGreeting}><img src="../images/save-icon.svg" alt=""/></Button>
                  <Button variant="secondary" className="account-btn row-btn-delete" onClick={handleToggleNewGreeting}><img src="../images/trash-icon.svg" alt=""/></Button>
                </div>
              </td>
            </tr>
          )}
          {renderGreetings(displayedGreetings)}
        </tbody>
      </Table>

      {!!filteredGreetings.length && (
        <Row className="justify-content-start">
          <Pagination className="justify-content-center">
            <Pagination.First disabled={displayPage === 1} onClick={() => setDisplayPage(1)} />
            <Pagination.Prev disabled={displayPage === 1} onClick={() => setDisplayPage(displayPage - 1)} />
            {renderPaginationItems()}
            <Pagination.Next disabled={displayPage === displayPages} onClick={() => setDisplayPage(displayPage + 1)} />
            <Pagination.Last disabled={displayPage === displayPages} onClick={() => setDisplayPage(displayPages)} />
          </Pagination>
        </Row>
      )}
    </>
  );
}

export default CompanyGreetingsTable;
